import React, { useState, useCallback, useEffect } from 'react';
import { getTimeline, postEntry, putEntry, deleteEntry } from '../lib/variableRateBonds';
import VariableRateChart from './VariableRateChart';

const today = () => new Date().toISOString().slice(0, 10);

const fmtRate = (n) =>
  n === null || n === undefined ? '-' : `${Number(n).toFixed(3)}%`;

const SOURCE_LABEL = {
  'feed-history': 'Feed (history)',
  'feed-scheduled': 'Feed (scheduled)',
  manual: 'Manual',
};

/**
 * Per-bond detail panel: step-line chart + mirrored entry list + inline point
 * editor + add-entry form. Every successful mutation re-renders from the server
 * by re-fetching the merged timeline (the entry endpoints return the single
 * created/updated/deleted entry, so we re-read the timeline for the source of
 * truth - no client echo), then calls onMutated() so the parent drops the list
 * cache and refetches.
 */
const VariableRateBondPanel = ({ bond, fetchWithAuth, onMutated, darkMode = false }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editor, setEditor] = useState(null); // { mode: 'edit'|'override', point }
  const [addRate, setAddRate] = useState('');
  const [addDate, setAddDate] = useState(today());
  const [pending, setPending] = useState(null); // { label, run: () => Promise, confirmLabel }
  const [busy, setBusy] = useState(false);

  const sid = bond.security_id;
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const rowBorder = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = `px-2 py-1 rounded border ${
    darkMode ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'
  }`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTimeline(await getTimeline(sid, fetchWithAuth));
    } catch (e) {
      setError(e.message || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [sid, fetchWithAuth]);

  useEffect(() => {
    load();
  }, [load]);

  // Runs a mutation, re-fetches the timeline from the server (the mutation
  // endpoints return only the affected entry), fires onMutated, and surfaces
  // errors as a banner. Never echoes client state.
  const runMutation = async (fn, successMsg) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      const fresh = await getTimeline(sid, fetchWithAuth);
      setTimeline(fresh);
      setEditor(null);
      setPending(null);
      setAddRate('');
      setNotice(successMsg);
      if (onMutated) onMutated();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const validRate = (raw) => {
    const r = parseFloat(raw);
    if (raw === '' || Number.isNaN(r)) return { ok: false, msg: 'Enter a numeric rate.' };
    if (r < 0 || r > 100) return { ok: false, msg: 'Rate must be between 0 and 100.' };
    return { ok: true, value: r };
  };

  const onAdd = () => {
    const v = validRate(addRate);
    if (!v.ok) {
      setError(v.msg);
      return;
    }
    if (!addDate) {
      setError('Pick an effective date.');
      return;
    }
    setPending({
      label: `Add ${v.value.toFixed(3)}% effective ${addDate}?`,
      confirmLabel: 'Confirm',
      run: () =>
        runMutation(
          () => postEntry(sid, { rate: v.value, effective_date: addDate }, fetchWithAuth),
          `Added ${v.value.toFixed(3)}% at ${addDate}`
        ),
    });
  };

  const onSaveEdit = () => {
    const v = validRate(editor.point.rate);
    if (!v.ok) {
      setError(v.msg);
      return;
    }
    const { point } = editor;
    runMutation(
      () =>
        putEntry(
          sid,
          point.entry_id,
          { rate: v.value, effective_date: point.effective_date },
          fetchWithAuth
        ),
      `Updated ${point.effective_date}`
    );
  };

  const onOverride = () => {
    const v = validRate(editor.point.rate);
    if (!v.ok) {
      setError(v.msg);
      return;
    }
    const { point } = editor;
    runMutation(
      () =>
        postEntry(sid, { rate: v.value, effective_date: point.effective_date }, fetchWithAuth),
      `Override at ${point.effective_date}`
    );
  };

  const onDelete = () => {
    const { point } = editor;
    setPending({
      label: `Delete the manual entry at ${point.effective_date}?`,
      confirmLabel: 'Confirm delete',
      run: () =>
        runMutation(
          () => deleteEntry(sid, point.entry_id, fetchWithAuth),
          `Deleted ${point.effective_date}`
        ),
    });
  };

  // Opens the editor pre-filled from the clicked point (editable rate for both
  // modes; date is fixed to the point's date - moving a date is delete + add).
  const openManualEditor = (point) => setEditor({ mode: 'edit', point: { ...point } });
  const openFeedEditor = (point) => setEditor({ mode: 'override', point: { ...point } });

  return (
    <div>
      {error && <div className="mb-3 px-3 py-2 rounded bg-red-100 text-red-800 text-sm">{error}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded bg-green-100 text-green-800 text-sm">{notice}</div>}

      {loading ? (
        <p className={muted}>Loading timeline...</p>
      ) : (
        <>
          <VariableRateChart
            timeline={timeline}
            onPointClick={openManualEditor}
            onFeedPointClick={openFeedEditor}
            darkMode={darkMode}
          />

          {/* Inline point editor */}
          {editor && (
            <div className={`mt-4 p-3 rounded border ${rowBorder}`}>
              <div className="text-sm font-semibold mb-2">
                {editor.mode === 'edit' ? 'Edit manual point' : 'Override feed point'} -{' '}
                {editor.point.effective_date}
              </div>
              <label className="text-sm mr-2" htmlFor="edit-rate">
                Edit rate (%)
              </label>
              <input
                id="edit-rate"
                type="number"
                step="0.001"
                min="0"
                max="100"
                className={`${inputCls} w-28 text-right`}
                value={editor.point.rate}
                onChange={(e) =>
                  setEditor((ed) => ({ ...ed, point: { ...ed.point, rate: e.target.value } }))
                }
              />
              <div className="mt-3 flex gap-2">
                {editor.mode === 'edit' ? (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={onSaveEdit}
                      className="px-3 py-1 rounded bg-blue-600 text-white text-xs disabled:opacity-50"
                    >
                      Save edit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={onDelete}
                      className="px-3 py-1 rounded bg-red-600 text-white text-xs disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onOverride}
                    className="px-3 py-1 rounded bg-blue-600 text-white text-xs disabled:opacity-50"
                  >
                    Override from this date
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className={`px-3 py-1 rounded text-xs ${muted} hover:underline`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add-entry form */}
          <div className={`mt-4 p-3 rounded border ${rowBorder}`}>
            <div className="text-sm font-semibold mb-2">Add rate entry</div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs mb-1" htmlFor="add-date">
                  Effective date
                </label>
                <input
                  id="add-date"
                  type="date"
                  className={inputCls}
                  value={addDate}
                  onChange={(e) => setAddDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs mb-1" htmlFor="add-rate">
                  New rate (%)
                </label>
                <input
                  id="add-rate"
                  type="number"
                  step="0.001"
                  min="0"
                  max="100"
                  className={`${inputCls} w-28 text-right`}
                  value={addRate}
                  onChange={(e) => setAddRate(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={onAdd}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
              >
                Add entry
              </button>
            </div>
          </div>

          {/* Confirmation prompt for add / delete */}
          {pending && (
            <div className={`mt-3 p-3 rounded border ${rowBorder} bg-yellow-50 text-yellow-900`}>
              <div className="text-sm mb-2">{pending.label}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={pending.run}
                  className="px-3 py-1 rounded bg-blue-600 text-white text-xs disabled:opacity-50"
                >
                  {pending.confirmLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="px-3 py-1 rounded text-xs text-gray-600 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Entry list mirrors the graph */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left border-b ${rowBorder} ${muted}`}>
                  <th className="py-2 pr-4">Effective date</th>
                  <th className="py-2 pr-4 text-right">Rate</th>
                  <th className="py-2 pr-4">Source</th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((p) => (
                  <tr
                    key={`${p.effective_date}-${p.source}-${p.entry_id ?? 'f'}`}
                    className={`border-b ${rowBorder} ${
                      p.source === 'manual' ? 'cursor-pointer hover:bg-muted/50' : ''
                    }`}
                    onClick={() => (p.source === 'manual' ? openManualEditor(p) : null)}
                  >
                    <td className="py-2 pr-4 font-mono">{p.effective_date}</td>
                    <td className="py-2 pr-4 text-right font-mono">{fmtRate(p.rate)}</td>
                    <td className="py-2 pr-4">{SOURCE_LABEL[p.source] || p.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default VariableRateBondPanel;
